import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Pagination } from '@mui/material';
import { styled } from '@mui/material/styles';
import FiltersCard from '../../common/filters/FiltersCard.jsx';
import ButtonExcelDownload from '../../common/buttons/ButtonExcelDownload.jsx';
import ButtonFilters from '../../common/buttons/ButtonFilters.jsx';
import ButtonsViews from '../../common/buttons/ButtonsViews.jsx';
import SortDropdown from '../../common/dropdowns/SortDropdown.jsx';
import FrameworkAgreementsList from './FrameworkAgreementsList.jsx';
import { useFrameworkAgreementsListStore } from './stores/useFrameworkAgreementsListStore.jsx';

const fetchJson = async (url) => {
    const response = await fetch(url);
    return response.json();
};

const FiltersActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    'a, button': {
        marginRight: '13px !important',
    },
});

export default function FrameworkAgreementsContent({
    context,
    societesId,
    membresId,
}) {

    const limit = 20;

    const { t: gctrad } = useTranslation();

    // TODO À mettre ailleurs ?
    const FRAMEWORK_AGREEMENTS_STATUS_TO_WRITE = 'fa_to_write';
    const FRAMEWORK_AGREEMENTS_STATUS_TO_BE_SIGNED = 'fa_to_be_signed';
    const FRAMEWORK_AGREEMENTS_STATUS_AWAITING_SIGNATURE = 'fa_awaiting_signature';
    const FRAMEWORK_AGREEMENTS_STATUS_SIGNED = 'fa_signed';
    const FRAMEWORK_AGREEMENTS_STATUS_TERMINATED = 'fa_terminated';
    const FRAMEWORK_AGREEMENTS_STATUS_CANCELLED = 'fa_cancelled';

    const sortOptions = [
        { value: 'agreement.date_created desc', label: gctrad('Date de création décroissante') },
        { value: 'agreement.date_created asc', label: gctrad('Date de création croissante') },
        { value: '', label: '⸺', disabled: true },
        { value: 'version.date_start asc', label: gctrad('Date de début croissante') },
        { value: 'version.date_start desc', label: gctrad('Date de début décroissante') },
        { value: '', label: '⸺', disabled: true },
        { value: 'version.date_end asc', label: gctrad('Date de fin croissante') },
        { value: 'version.date_end desc', label: gctrad('Date de fin décroissante') },
        { value: '', label: '⸺', disabled: true },
        { value: 'agreement.title', label: gctrad('Titre du contrat') },
    ];

    const defaultOrder = sortOptions[0].value;

    const [openFilters, setOpenFilters] = useState(false);
    const [managersList, setManagersList] = useState([]);
    const [xlsExportUrl, setXlsExportUrl] = useState(null);

    // Liste des statuts des contrats
    const statusesItems = [
        'providers' == context && {
            'value': FRAMEWORK_AGREEMENTS_STATUS_TO_WRITE,
            'label': gctrad('A rédiger'),
        },
        'providers' == context && {
            'value': FRAMEWORK_AGREEMENTS_STATUS_TO_BE_SIGNED,
            'label': gctrad('A envoyer au client'),
        },
        {
            'value': FRAMEWORK_AGREEMENTS_STATUS_AWAITING_SIGNATURE,
            'label': 'providers' == context ? gctrad('Proposition envoyée') : gctrad('Proposition reçue'),
        },
        {
            'value': FRAMEWORK_AGREEMENTS_STATUS_SIGNED,
            'label': gctrad('Signé'),
        },
        {
            'value': FRAMEWORK_AGREEMENTS_STATUS_TERMINATED,
            'label': gctrad('Résilié'),
        },
        {
            'value': FRAMEWORK_AGREEMENTS_STATUS_CANCELLED,
            'label': gctrad('Annulé'),
        }
    ].filter((item) => !!item);

    const viewButtons = [
        // { 'title': 'view_card', 'slug': 'list', 'icon': 'fa-regular fa-table-rows' },
        // { 'title': 'view_stats', 'slug': 'stats', 'icon': 'fa-regular fa-chart-simple' }
    ];

    const {
        order,
        offset,
        page,
        setOrder,
        setPage,
    } = useFrameworkAgreementsListStore();

    const {
        selectedStatus,
        selectedManager,
        setSelectedStatus,
        setSelectedManager,
    } = useFrameworkAgreementsListStore();

    const {
        search,
        setSearch,
        handleSearch,
    } = useFrameworkAgreementsListStore();

    const hasFilters = useMemo(() => (
        !!selectedStatus.length ||
        !!selectedManager.length
    ), [
        selectedStatus,
        selectedManager,
    ]);

    const [quit, setQuit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [frameworkAgreements, setFrameworkAgreements] = useState([]);
    const [total, setTotal] = useState(0);

    const paginationPageCount = useMemo(
        () => Math.ceil(total / limit),
        [total],
    );

    const loadData = () => {

        if ( quit ) return;

        setLoading(true);

        // Chargement des responsables

        if('clients' !== context) {
            fetchJson(`${ENV.base_lang_url}myspace/${context}//framework_agreements/ajax_get_managers`)
            .then(response => {
                let managers = [];
                response.managers.forEach(manager => managers.push({value: manager.id, label: `${manager.surname} ${manager.name}`.trim()}))
                setManagersList(managers);
            });
        } // endif

        // URL de chargement des contrats pour l'affichage
        let url = new URL(`${ENV.base_lang_url}myspace/${context}//framework_agreements/ajax_get_list`);
        url.searchParams.append('societe_id', societesId);
        if (search) url.searchParams.append('search', search);
        url.searchParams.append('order', order || defaultOrder);
        url.searchParams.append('limit', limit);
        url.searchParams.append('offset', offset || 0);
        selectedStatus.forEach(item => url.searchParams.append('status[]', item.value));
        selectedManager.forEach(item => url.searchParams.append('manager_id[]', item.value));
        url.searchParams.set('with_total', true);
        url.searchParams.set('with_kpis', true);
        if ( 'clients' == context ) url.searchParams.set('client_id', membresId);

        // URL de chargement des contrats pour l'export XLS
        let xlsUrl = new URL(url);
        xlsUrl.searchParams.delete('limit');
        xlsUrl.searchParams.delete('offset');
        xlsUrl.searchParams.delete('with_total');
        xlsUrl.searchParams.set('format', 'xls');
        setXlsExportUrl(xlsUrl.toString());

        // Chargement des contrats
        fetchJson(url.toString())
            .then(response => {
                setFrameworkAgreements(response.frameworkAgreements);
                setTotal(response.total);
                setLoading(false);
            });
            
    };

    useEffect(loadData, [order, offset, selectedStatus, selectedManager, search]);

    useEffect(() => {
        setOpenFilters(hasFilters);
    }, [hasFilters]);

    /**
     * Modification de l'ordre de tri
     */
    const handleSortChange = (value) => {

        setLoading(true);
        setOrder(value);
        setPage(1);

    } // end of 'handleSortChange()'

    /**
     * Modification de la valeur d'un filtre
     */
    const handleFilterChange = (value, filter) => {

        setLoading(true);
        setPage(1);

        switch (filter) {

            case 'status':
                setSelectedStatus(value);
            break;

            case 'manager':
                setSelectedManager(value);
            break;

        } // endswitch

    } // end of 'handleFilterChange()'

    /**
     * Suppression de tous les filtres
     */
    const handleFilterClear = () => {

        setLoading(true);
        setPage(1);
        setSelectedStatus([]);
        setSelectedManager([]);

    } // end of 'handleFilterClear()'

    /**
     * Changement de page
     */
    const handlePaginationChange = (event, page) => {

        setLoading(true);
        setPage(page);
        
    } // end of 'handlePaginationChange()'

    /**
     * Suppression des paramètres mémorisés quand on quitte la liste des contrats
     */
    window.clearFrameworkAgreementsListStore = () => {

        setQuit(true);
        setOrder(null);
        setPage(1);
        setSelectedStatus([]);
        setSelectedManager([]);
        setSearch(null);
        useFrameworkAgreementsListStore.persist.clearStorage();
        
    } // end of 'clearFrameworkAgreementsListStore()'

    /**
     * Recherche globale
     */
    document.getElementById('global_search_remove_filter').removeEventListener('click', handleSearch);
    document.getElementById('global_search_remove_filter').addEventListener('click', handleSearch);
    document.getElementById('global_search').removeEventListener('input', handleSearch);
    document.getElementById('global_search').addEventListener('input', handleSearch);
    document.getElementById('global_search').removeAttribute('readonly');
    document.getElementById('global_search').value = search;
    if ( search ) document.getElementById('global_search_remove_filter').classList.remove('d-none');

    return (
        <div className="framework_agreements-content">

            {/* Barre d'outils */}
            <div className="d-flex justify-content-between mb-3">

                <FiltersActions>

                    <ButtonExcelDownload 
                        title={formatXlsExportFilename(currentSociete.nom, gctrad('Contrats cadre'))}
                        dataUrl={xlsExportUrl}
                    />

                    <SortDropdown 
                        options={sortOptions}
                        selectedOption={order || defaultOrder}
                        selectedLabel={sortOptions.find((option) => (order || defaultOrder) == option.value)?.label}
                        parentFunction={handleSortChange}
                    />

                    <ButtonFilters 
                        open={openFilters} 
                        hasFilters={hasFilters}
                        onToggleCollapse={(value) => setOpenFilters(value)}
                        onFiltersClear={handleFilterClear}
                    />

                    <span className="d-none d-md-inline-block text-gc-dark-blue font-weight-bold">
                        {'nb_results'.trad().replace('%1', loading ? '-' : total)}
                    </span>			

                </FiltersActions>

                <div className="d-flex justify-content-between">

                    <div className="d-flex align-items-center">

                        {!!viewButtons.length &&
                            <ButtonsViews
                                items={viewButtons}
                                value="list"
                                handleChange={() => { return; }}
                            />
                        }

                        {'providers' == context &&
                            <a
                                className="btn btn-gc-orange js-nav font-weight-bold get_part_link ml-3"
                                href="#"
                                data-part="+"
                                data-target={'providers//framework_agreements/create'}
                            >
                                {gctrad('Ajouter un contrat')}
                            </a>
                        }

                    </div>

                </div>

            </div>

            {/* Panneau des filtres */}
            <FiltersCard
                open={openFilters}
                parentFunction={(handleFilterChange)} 
                inputs={[
                    {
                        label: gctrad('État des contrats'),
                        type: 'multipleSelect',
                        slug: 'status',
                        data: statusesItems,
                        selectedItems: selectedStatus,
                    },
                    {
                        label: gctrad('Responsable'),
                        type: 'multipleSelect',
                        slug: 'manager',
                        data: managersList,
                        selectedItems: selectedManager,
                        hide: 'clients' == context,
                    }
                ]}
            >
            </FiltersCard>

            {/* Liste des contrats */}
            <div>
                <FrameworkAgreementsList
                    context={context}
                    societesId={societesId}
                    frameworkAgreements={frameworkAgreements}
                    loading={loading}
                />
                {!loading && ( 1 < paginationPageCount ) && 
                    <Pagination
                        count={paginationPageCount}
                        page={page}
                        shape="rounded"
                        onChange={handlePaginationChange}
                    />
                }
            </div>

        </div>
    );

}
